import React, { useState, useEffect, useRef } from 'react';
import {
  Box, Stack, Typography, TextField, MenuItem, Select, InputLabel,
  FormControl, Switch, FormControlLabel, Button,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Stepper, Step, StepLabel, StepContent, Paper, Grid,
  FormHelperText
} from '@mui/material';

import dayjs from 'dayjs';
import TariffService from '../TariffService';
import RatingService from '../RatingService';
import DecimalInput from '../UiComponents/DecimalInput';
import { formatNumber } from '../Utils/Converters';
import SubMeterManager from './SubMeterManager';
import TariffSelector from '../TariffSelector';
import logService, { sendFrontendLog } from '../logService';

const FreeConsumptionStepper = () => {
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [tariffId, setTariffId] = useState('');
  const [tariffs, setTariffs] = useState([]);
  const [indexTypes, setIndexTypes] = useState([]);
  const [mainMeterConsumptions, setMainMeterConsumptions] = useState({});
  const [subsidized, setSubsidized] = useState(true);

  const [activeStep, setActiveStep] = useState(0);
  const [subMeters, setSubMeters] = useState([]);
  const [result, setResult] = useState(null);

  const [contractPowerChargeSharing, setContractPowerChargeSharing] = useState(null);
  const [exceededPowerChargeSharing, setExceededPowerChargeSharing] = useState(null);


  const prevTariffIdRef = useRef(null);

  const [errors, setErrors] = useState({
    startDate: false,
    endDate: false,
    tariffId: false
  });


  useEffect(() => {
    TariffService.getTariffs().then(res => setTariffs(res.data));
  }, []);

  useEffect(() => {
    if (tariffId) {
      TariffService.getInvoiceIndexTypesByTariffId(tariffId).then(res => {
        setIndexTypes(res.data);
        setMainMeterConsumptions({});
      });
    }
  }, [tariffId]);

  useEffect(() => {
    if (prevTariffIdRef.current !== tariffId) {
      setSubMeters([]);
      prevTariffIdRef.current = tariffId;
    }
  }, [tariffId]);

  const handleMainMeterInputChange = (type, value) => {
    setMainMeterConsumptions(prev => ({ ...prev, [type]: value }));
  };

  const buildRequest = () => {
    const mainMeterList = indexTypes
      .map(it => ({
        indexType: it.indexType,
        consumption: parseFloat(mainMeterConsumptions[it.indexType] || 0)
      }))
      .filter(c => c.consumption > 0);

    const subMeterList = subMeters.map(meter => ({
      subMeterId: meter.subMeterId,
      priority: meter.priority,
      consumptions: Object.entries(meter.consumptions || {}).map(([indexType, value]) => ({
        indexType,
        consumption: parseFloat(value)
      }))
    }));

    return {
      startDate: dayjs(startDate).format('YYYY-MM-DD'),
      endDate: dayjs(endDate).format('YYYY-MM-DD'),
      tariffId,
      subsidized,
      contractPowerChargeSharing,
      exceededPowerChargeSharing,

      mainMeterConsumptions: mainMeterList,
      subMeterConsumptions: subMeterList
    };
  };

  const handleCalculate = async () => {
    logService.sendFrontendLog('handleCalculate geldi');
    const req = buildRequest();
    logService.sendFrontendLog('buildRequest oncesi');
    const res = await RatingService.rating(req);
    logService.sendFrontendLog('buildRequest sonrasi');
    setResult(res.data);
    logService.sendFrontendLog('setResult sonrasi');

    setActiveStep(prev => prev + 1);
    logService.sendFrontendLog('setActiveStep sonrasi');

  };

  return (
    <Box sx={{ maxWidth: { xs: '100%', sm: 600 }, mx: 'auto' }}>
      <Stepper activeStep={activeStep} orientation="vertical">
        <Step>
          <StepLabel
            optional={
              <Typography variant="caption">Faturadaki temel bilgiler</Typography>
            }
          >
            Fatura Bilgileri
          </StepLabel>
          <StepContent>
            <Box p={1}>


              <TariffSelector
                tariffs={tariffs}
                value={tariffId}
                onChange={(tariff) => {
                  setTariffId(tariff?.tariffId);  // eski kod uyumluluğu
                }}
                error={Boolean(errors.tariffId)}
                helperText={errors.tariffId && "Tarife seçimi zorunludur"}
                disabled={false}
              />

              <FormControl fullWidth sx={{ mt: 2 }}>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>

                    {indexTypes.find(i => i.indexType === 'P') && (
                      <FormControl fullWidth>
                        <InputLabel>Sözleşme Bedelini Kim Ödesin?</InputLabel>
                        <Select
                          value={contractPowerChargeSharing || ''}
                          onChange={(e) => setContractPowerChargeSharing(e.target.value)}
                          label="Sözleşme Bedelini Kim Ödesin?"
                        >
                          <MenuItem value="MAIN_METER_ONLY">Ana sayaç ödesin</MenuItem>
                          <MenuItem value="SUB_METERS_ONLY">Süzme sayaçlar eşit paylaşsın</MenuItem>
                          <MenuItem value="SHARED_BETWEEN_ALL">Ana sayaç ve süzme sayaçlar eşit paylaşsın</MenuItem>
                        </Select>
                      </FormControl>
                    )}

                  </Grid>
                  <Grid item xs={12} sm={6}>

                    {indexTypes.find(i => i.indexType === 'D') && (
                      <FormControl fullWidth>
                        <InputLabel>Güç Aşım Bedelini Kim Ödesin?</InputLabel>
                        <Select
                          value={exceededPowerChargeSharing || ''}
                          onChange={(e) => setExceededPowerChargeSharing(e.target.value)}
                          label="Güç Aşım Bedelini Kim Ödesin?"
                        >
                          <MenuItem value="MAIN_METER_ONLY">Ana sayaç ödesin</MenuItem>
                          <MenuItem value="SUB_METERS_ONLY">Süzme sayaçlar eşit paylaşsın</MenuItem>
                          <MenuItem value="SHARED_BETWEEN_ALL">Ana sayaç ve süzme sayaçlar eşit paylaşsın</MenuItem>
                        </Select>

                      </FormControl>
                    )}
                  </Grid>
                </Grid>
              </FormControl>

              <Grid container spacing={2} sx={{ mt: 1 }}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    required
                    label="İlk okuma tarihi"
                    type="date"
                    fullWidth
                    value={startDate}
                    onChange={e => setStartDate(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    error={errors.startDate}
                    helperText={errors.startDate ? 'Başlangıç tarihi zorunludur' : ''}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    required
                    label="Son okuma tarihi"
                    type="date"
                    fullWidth
                    value={endDate}
                    onChange={e => setEndDate(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    error={errors.endDate}
                    helperText={
                      !endDate
                        ? 'Bitiş tarihi zorunludur'
                        : new Date(endDate) <= new Date(startDate)
                          ? 'Bitiş tarihi, başlangıçtan sonra olmalıdır'
                          : ''
                    }
                  />
                </Grid>
              </Grid>

            </Box>
            <Box sx={{ mb: 2 }}>
              <Button
                variant="contained"
                onClick={() => {
                  logService.sendFrontendLog('Tuketim Bilgileri Gir butonuna tiklandi');
                  const isStartDateValid = Boolean(startDate);
                  const isEndDateValid = Boolean(endDate);
                  const isTariffValid = Boolean(tariffId);

                  const isDateOrderValid =
                    isStartDateValid &&
                    isEndDateValid &&
                    new Date(endDate) > new Date(startDate); // Sıralama kontrolü

                  const newErrors = {
                    startDate: !isStartDateValid,
                    endDate: !isEndDateValid || !isDateOrderValid,
                    tariffId: !isTariffValid
                  };
                  if (Object.values(newErrors).some(Boolean)) {
                    logService.sendFrontendLog('Form gecersiz', 'warn');
                  }

                  setErrors(newErrors);

                  const hasError = Object.values(newErrors).some(Boolean);
                  if (hasError) {
                    logService.sendFrontendLog('hasError', 'warn');
                    return;
                  }
                  logService.sendFrontendLog('Form gecerli, Step 1e geciliyor');
                  setActiveStep(1);
                  logService.sendFrontendLog('Step 1e gecildi');
                }}
                sx={{ mt: 1, mr: 1 }}
              >
                Tüketim Bilgileri Gir
              </Button>

            </Box>
          </StepContent>
        </Step>

        <Step>
          <StepLabel
            optional={
              <Typography variant="caption">Ana sayacın faturadaki tüketimlerini girin</Typography>
            }
          >
            Tüketim Bilgileri
          </StepLabel>
          <StepContent>
            <Box p={1}>
              <Stack spacing={1.5}>
                {indexTypes.map(it => (
                  <DecimalInput
                    key={it.indexType}
                    label={it.indexName}
                    value={mainMeterConsumptions[it.indexType] || ''}
                    onChange={(val) => handleMainMeterInputChange(it.indexType, val)}
                    decimalSeparator="," maxDecimalDigits={3} unit={it.unit}
                  />
                ))}
              </Stack>
            </Box>
            <Box sx={{ mb: 2 }}>
              <Button variant="contained" onClick=
                {() => {
                  logService.sendFrontendLog('Suzme sayaclari Gir butonuna tiklandi');

                  handleCalculate()
                }}
                sx={{ mt: 1, mr: 1 }}>
                Süzme Sayaçları Gir
              </Button>
              <Button onClick={() => setActiveStep(0)} sx={{ mt: 1, mr: 1 }}>
                Geri
              </Button>
            </Box>
          </StepContent>
        </Step>

        <Step>
          <StepLabel
            optional={
              <Typography variant="caption">Süzme sayaçların payını hesaplayın</Typography>
            }
          >
            Süzme Sayaçlar
          </StepLabel>
          <StepContent>
            {Boolean(tariffId) && (
              <SubMeterManager
                indexTypes={indexTypes}
                subMeters={subMeters}
                setSubMeters={setSubMeters}
                tariffId={tariffId}
                buildRequest={buildRequest}
                setResult={setResult}
                result={result}
              />)}
            <Button onClick={() => setActiveStep(1)} sx={{ mt: 1, mr: 1 }}>
              Geri
            </Button>

          </StepContent>
        </Step>
      </Stepper>
    </Box>
  );
};

export default FreeConsumptionStepper;
